import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import FeeStructureModal from "../../../components/Modals/FeeStructureModal";
import BankDetailsModal from "../../../components/Modals/BankDetailsModal";
import EnhancedTable from "../../../components/common/EnhancedTable";
import ActionButtons from "../../../components/common/ActionButtons";
import {
  useFeeStructure,
  useFeeStructureBySessionAndClass,
  FeeStructureDto,
  BankDetailsDto,
} from "../../../hooks/useFeeStructure";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { useSchoolIdForAdmin } from "../../../hooks/useSchoolIdForAdmin";
import { useError } from "../../../contexts/ErrorContext";
import { useClasses } from "../../../hooks/useClasses";
import { useSessions } from "../../../hooks/useSession";
import { useSchools } from "../../../hooks/useSchools";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { format } from "date-fns";

const FeeStructureManagement: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [allViewModalOpen, setAllViewModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bankDetailsModalOpen, setBankDetailsModalOpen] = useState(false);
  const [currentFeeStructure, setCurrentFeeStructure] = useState<
    FeeStructureDto | undefined
  >(undefined);
  const [feeStructureToDelete, setFeeStructureToDelete] = useState<
    FeeStructureDto | null
  >(null);

  const { selectedSession, selectedClass } = useSessionClass();
  const { t } = useTranslation();
  const { user, isTeacher, isSchoolAdmin } = useAuth();
  const { showError, showSuccess } = useError();
  // Disable API call for school admin - they already have schoolId from user context
  const { data: schoolIdFromAdmin, isLoading: schoolIdLoading } =
    useSchoolIdForAdmin(isSchoolAdmin() ? undefined : user?.id);
  const queryClient = useQueryClient();

  const schoolId = useMemo(() => {
    if (isTeacher()) {
      return user?.schoolId;
    }
    if (user?.schoolId) {
      return user.schoolId;
    }
    return schoolIdFromAdmin;
  }, [user?.schoolId, schoolIdFromAdmin, isTeacher]);

  const {
    createFeeStructureMutation,
    updateFeeStructureMutation,
    deleteFeeStructureMutation,
    addBankDetailsMutation,
  } = useFeeStructure(schoolId, false, false); // Disable getAll query - we use getBySessionAndClass instead

  // Fetch classes and sessions to map IDs to names
  const { classesQuery } = useClasses(schoolId);
  const { sessionsQuery } = useSessions(schoolId);
  const { useSchoolById, useSchoolLogoInfo } = useSchools();

  const classes = classesQuery.data || [];
  const sessions = sessionsQuery.data || [];
  const hasClasses = classes.length > 0;
  
  // Fetch school data for logo and name
  const { data: schoolData } = useSchoolById(schoolId);
  const { data: logoInfo } = useSchoolLogoInfo(schoolId);
  
  const [schoolLogoUrl, setSchoolLogoUrl] = useState<string | null>(null);
  
  // Load school logo once; if no logo is configured, just show the school name (no default logo)
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        if (logoInfo && logoInfo.fileName) {
          const url = `${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`;
          setSchoolLogoUrl(url);
          if (schoolId) {
            localStorage.setItem(`school_logo_${schoolId}`, url);
          }
          return;
        }

        if (schoolId) {
          const cached = localStorage.getItem(`school_logo_${schoolId}`);
          if (cached) {
            setSchoolLogoUrl(cached);
            return;
          }
        }

        // No logo present – do not render any image
        setSchoolLogoUrl(null);
      } catch (err) {
        setSchoolLogoUrl(null);
      }
    };

    if (schoolId && logoInfo !== undefined) {
      fetchLogo();
    }
  }, [schoolId, logoInfo]);

  // Create lookup maps for class and session names
  const classMap = useMemo(() => {
    const map = new Map<number, string>();
    classes.forEach((cls) => {
      if (cls.id) {
        const displayName = cls.section ? `${cls.name} ${cls.section}` : cls.name;
        map.set(cls.id, displayName);
      }
    });
    return map;
  }, [classes]);

  const sessionMap = useMemo(() => {
    const map = new Map<number, string>();
    sessions.forEach((session) => {
      if (session.id) {
        map.set(session.id, session.name);
      }
    });
    return map;
  }, [sessions]);

  const getBaseClassName = useCallback((name?: string | null) => {
    if (!name) return "-";
    return name.split(" ")[0];
  }, []);

  // Get sessionId and className for the API call
  const sessionId = selectedSession?.id;
  const className = useMemo(() => {
    if (!selectedClass?.name) return undefined;
    return getBaseClassName(selectedClass.name);
  }, [selectedClass?.name, getBaseClassName]);

  // Only enable fee structure query when classes and sessions exist AND session/class are selected
  const hasSessions = sessions.length > 0;
  const shouldFetchFeeStructure = 
    hasClasses && 
    hasSessions && 
    !!sessionId && 
    !!className;

  // Fetch fee structure using getBySessionAndClass endpoint
  const { data: feeStructureData, isLoading: feeStructureLoading, isError: feeStructureError } = 
    useFeeStructureBySessionAndClass(
      sessionId,
      className,
      shouldFetchFeeStructure // Enable only when classes/sessions exist and both are selected
    );

  // Convert single FeeStructureDto to array for table display
  const feeStructures = useMemo(() => {
    if (!feeStructureData) {
      return [];
    }
    // Convert single fee structure to array format
    return [{
      ...feeStructureData,
      className: getBaseClassName(
        feeStructureData.className ||
          (feeStructureData.classId ? classMap.get(feeStructureData.classId) || "-" : "-")
      ),
      sessionName: feeStructureData.sessionName || 
        (feeStructureData.sessionId ? sessionMap.get(feeStructureData.sessionId) || "-" : "-"),
    }];
  }, [feeStructureData, classMap, sessionMap, getBaseClassName]);

  const handleOpenAddModal = useCallback(() => {
    setIsEdit(false);
    setCurrentFeeStructure(undefined);
    setModalOpen(true);
  }, []);

  const handleOpenEditModal = useCallback((feeStructure: FeeStructureDto) => {
    setIsEdit(true);
    setCurrentFeeStructure(feeStructure);
    setModalOpen(true);
  }, []);

  const handleOpenViewModal = useCallback((feeStructure: FeeStructureDto) => {
    setCurrentFeeStructure(feeStructure);
    setViewModalOpen(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setCurrentFeeStructure(undefined);
  }, []);

  const handleOpenAllViewModal = useCallback(() => {
    setAllViewModalOpen(true);
  }, []);

  const handleCloseAllViewModal = useCallback(() => {
    setAllViewModalOpen(false);
  }, []);

  const handleOpenDeleteDialog = useCallback((feeStructure: FeeStructureDto) => {
    setFeeStructureToDelete(feeStructure);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setFeeStructureToDelete(null);
  }, []);

  const handleOpenBankDetailsModal = useCallback(() => {
    setBankDetailsModalOpen(true);
  }, []);

  const handleCloseBankDetailsModal = useCallback(() => {
    setBankDetailsModalOpen(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setCurrentFeeStructure(undefined);
  }, []);

  const handleSubmit = useCallback(
    async (formData: FeeStructureDto) => {
      try {
        // Prevent duplicate fee structure for same base class within same session
        const selectedClassName = classMap.get(formData.classId) || "";
        const baseClassName = getBaseClassName(selectedClassName);
        const duplicate = feeStructures.some(
          (fs) =>
            getBaseClassName(fs.className) === baseClassName &&
            fs.sessionId === formData.sessionId &&
            (!isEdit || fs.classId !== formData.classId || fs.sessionId !== formData.sessionId)
        );
        if (duplicate) {
          showError(
            t("fee_structure_duplicate_class") ||
              "Fee structure already exists for this class"
          );
          return;
        }

        // Ensure className is included in formData
        const formDataWithClassName: FeeStructureDto = {
          ...formData,
          className: formData.className || baseClassName,
        };

        if (isEdit) {
          await updateFeeStructureMutation.mutateAsync(formDataWithClassName);
          showSuccess(
            t("fee_structure_updated_successfully") ||
              "Fee structure updated successfully"
          );
          handleCloseModal();
        } else {
          await createFeeStructureMutation.mutateAsync(formDataWithClassName);
          showSuccess(
            t("fee_structure_added_successfully") ||
              "Fee structure added successfully"
          );
          handleCloseModal();
        }
      } catch (error: any) {
        showError(
          error?.response?.data?.message ||
            (isEdit 
              ? (t("error_updating_fee_structure") || "Error updating fee structure")
              : (t("error_adding_fee_structure") || "Error adding fee structure"))
        );
      }
    },
    [isEdit, createFeeStructureMutation, updateFeeStructureMutation, handleCloseModal, showError, showSuccess, t, classMap, getBaseClassName, feeStructures]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!feeStructureToDelete) return;

    try {
      await deleteFeeStructureMutation.mutateAsync({
        sessionId: feeStructureToDelete.sessionId,
        classId: feeStructureToDelete.classId,
        feeType: feeStructureToDelete.feeType,
      });
      showSuccess(
        t("fee_structure_deleted_successfully") ||
          "Fee structure deleted successfully"
      );
      handleCloseDeleteDialog();
    } catch (error: any) {
      showError(
        error?.response?.data?.message ||
          t("error_deleting_fee_structure") ||
          "Error deleting fee structure"
      );
    }
  }, [feeStructureToDelete, deleteFeeStructureMutation, handleCloseDeleteDialog, showError, showSuccess, t]);

  const handleSubmitBankDetails = useCallback(
    async (formData: BankDetailsDto) => {
      try {
        await addBankDetailsMutation.mutateAsync(formData);
        showSuccess(
          t("bank_details_added_successfully") ||
            "Bank details added successfully"
        );
        handleCloseBankDetailsModal();
      } catch (error: any) {
        showError(
          error?.response?.data?.message ||
            t("error_adding_bank_details") ||
            "Error adding bank details"
        );
      }
    },
    [addBankDetailsMutation, handleCloseBankDetailsModal, showError, showSuccess, t]
  );

  // Show loading state only while queries are actually loading
  // Don't show loader if queries have finished but returned empty data
  const isActuallyLoading = 
    (classesQuery.isLoading && !classesQuery.data) || 
    (sessionsQuery.isLoading && !sessionsQuery.data) ||
    (feeStructureLoading && shouldFetchFeeStructure);

  if (isActuallyLoading) {
    return (
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("fee_structure_management") || "Fee Structure Management"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleOpenAllViewModal}
              disabled={!hasClasses}
            >
              {t("view_all_fee_structures") || "View All Fee Structures"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleOpenBankDetailsModal}
              disabled={!hasClasses}
              sx={{
                borderColor: "#3092e3",
                color: "#3092e3",
                "&:hover": { 
                  borderColor: "#1976d2",
                  backgroundColor: "rgba(48, 146, 227, 0.04)",
                },
              }}
            >
              {t("add_bank_details") || "Add Bank Details"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" },
              }}
              onClick={handleOpenAddModal}
              disabled={!hasClasses}
            >
              {t("add_fee_structure") || "Add Fee Structure"}
            </Button>
          </Box>
        </Box>

        {feeStructureError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("error_loading_fee_structures") || "Error loading fee structures"}
          </Alert>
        )}
        {hasClasses && hasSessions && (!sessionId || !className) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("please_select_session_and_class") || "Please select a session and class to view fee structure"}
          </Alert>
        )}

        <EnhancedTable
          columns={[
            { 
              id: "feeType", 
              label: t("fee_type") || "Fee Type", 
              minWidth: 150,
              format: (value: string) => {
                if (value === "TUTION_FEES") {
                  return "Tution Fees";
                }
                return value || "-";
              }
            },
            { id: "className", label: t("class") || "Class", minWidth: 150 },
            { id: "sessionName", label: t("session") || "Session", minWidth: 150 },
            { id: "totalInstallments", label: t("total_installments") || "Total Installments", minWidth: 150, align: "center" },
          ]}
          data={feeStructures.map((feeStructure) => ({
            ...feeStructure,
            feeType: feeStructure.feeType || "-",
            totalInstallments: feeStructure.totalInstallments || 0,
          }))}
          searchFields={["feeType", "className", "sessionName"]}
          filterFields={[]}
          actions={(row: FeeStructureDto) => (
            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
              <ActionButtons
                onView={() => handleOpenViewModal(row)}
                onEdit={() => handleOpenEditModal(row)}
                onDelete={() => handleOpenDeleteDialog(row)}
                showView={true}
                showEdit={true}
                showDelete={true}
                viewTooltip={t("view") || "View"}
                editTooltip={t("edit") || "Edit"}
                deleteTooltip={t("delete") || "Delete"}
              />
            </Box>
          )}
          cardProps={{
            elevation: 0,
            sx: { 
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent'
            }
          }}
        />
      </Paper>

      <FeeStructureModal
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentFeeStructure}
        onSubmit={handleSubmit}
        isEdit={isEdit}
      />

      <BankDetailsModal
        open={bankDetailsModalOpen}
        handleClose={handleCloseBankDetailsModal}
        onSubmit={handleSubmitBankDetails}
        schoolId={schoolId}
      />

      {/* View Fee Structure Dialog */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f8f9fb",
            borderBottom: "2px solid #e0e7ff",
            pb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ color: "#1a237e" }}>
            {t("fee_structure_details") || "Fee Structure Details"}
          </Typography>
          <IconButton onClick={handleCloseViewModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: "#ffffff" }}>
          {currentFeeStructure && (
            <Box sx={{ p: 4 }}>
              {/* School Logo, Name and Details */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}>
                {schoolLogoUrl && (
                  <Box
                    component="img"
                    src={schoolLogoUrl}
                    alt="School Logo"
                    sx={{
                      height: 80,
                      width: "auto",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      // If logo fails to load, hide the image and avoid further requests
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                      color: "#1a237e",
                      fontSize: "40px",
                      mb: 0.5,
                    }}
                  >
                    {schoolData?.nameofSchool?.toUpperCase() || "SCHOOL NAME"}
                  </Typography>
                  {(schoolData?.schoolCode || schoolData?.diseCode) && (
                    <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                      {schoolData?.schoolCode && (
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "18px" }}>
                          <strong>School Code:</strong> {schoolData.schoolCode}
                        </Typography>
                      )}
                      {schoolData?.diseCode && (
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "18px" }}>
                          <strong>DISE Code:</strong> {schoolData.diseCode}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* FEE STRUCTURE Title - Centered */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{
                    color: "#1a237e",
                    fontSize: "30px",
                    textAlign: "center",
                  }}
                >
                  FEE STRUCTURE
                </Typography>
              </Box>

              {/* Info Bar with CLASS, SESSION, FEE TYPE, TOTAL ANNUAL FEES */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 4,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: "#666" }}>
                    {t("class") || "Class"}:
                  </Typography>
                  <Typography variant="body1" fontWeight="normal" sx={{ color: "#333" }}>
                    {currentFeeStructure.className || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: "#666" }}>
                    {t("session") || "Session"}:
                  </Typography>
                  <Typography variant="body1" fontWeight="normal" sx={{ color: "#333" }}>
                    {currentFeeStructure.sessionName || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: "#666" }}>
                    {t("fee_type") || "Fee Type"}:
                  </Typography>
                  <Typography variant="body1" fontWeight="normal" sx={{ color: "#333" }}>
                    {currentFeeStructure.feeType === "TUTION_FEES" ? "TUTION FEES" : currentFeeStructure.feeType || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: "#666" }}>
                    {t("total_annual_fees") || "Total Annual Fees"}:
                  </Typography>
                  <Typography variant="body1" fontWeight="normal" sx={{ color: "#1a237e" }}>
                    ₹{currentFeeStructure.totalAnnualFees?.toLocaleString("en-IN") || "0"}
                  </Typography>
                </Box>
              </Box>

              {/* Installments Table */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    color: "#1a237e",
                    mb: 2,
                  }}
                >
                  {t("fee_installments") || "FEE INSTALLMENTS"}
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    borderRadius: 2,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#1976d2" }}>
                        <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.95rem" }}>
                          {t("installment_number") || "INSTALLMENT #"}
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.95rem" }}>
                          {t("fee_type") || "FEE TYPE"}
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.95rem" }}>
                          {t("amount") || "AMOUNT (₹)"}
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.95rem" }}>
                          {t("due_date") || "DUE DATE"}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentFeeStructure.feeInstallments && currentFeeStructure.feeInstallments.length > 0 ? (
                        currentFeeStructure.feeInstallments
                          .sort((a, b) => a.installmentNumber - b.installmentNumber)
                          .map((installment, index) => {
                            // Safely format date - handle array format from Java LocalDateTime
                            let formattedDate = "-";
                            
                            if (installment.dueDate) {
                              try {
                                let date: Date | null = null;
                                const dueDateValue = installment.dueDate as any;
                                
                                // Check if it's an array format (Java LocalDateTime)
                                if (Array.isArray(dueDateValue)) {
                                  // Array format: [year, month, day, hour, minute, second?, nanoseconds?]
                                  // Note: month in array is 1-indexed, but JavaScript Date uses 0-indexed months
                                  const [year, month, day, hour = 0, minute = 0, second = 0] = dueDateValue;
                                  date = new Date(year, month - 1, day, hour, minute, second);
                                } else if (dueDateValue instanceof Date) {
                                  date = dueDateValue;
                                } else if (typeof dueDateValue === 'string') {
                                  date = new Date(dueDateValue);
                                  if (isNaN(date.getTime())) {
                                    const timestamp = parseInt(dueDateValue);
                                    if (!isNaN(timestamp)) {
                                      date = new Date(timestamp);
                                    }
                                  }
                                } else if (typeof dueDateValue === 'number') {
                                  date = new Date(dueDateValue);
                                }
                                
                                if (date && !isNaN(date.getTime())) {
                                  formattedDate = format(date, "dd/MM/yyyy");
                                }
                              } catch (error) {
                              }
                            }
                            
                            // Format fee type for display
                            const feeTypeDisplay = currentFeeStructure.feeType === "TUTION_FEES" 
                              ? "Tution Fee" 
                              : currentFeeStructure.feeType || "-";
                            
                            return (
                              <TableRow 
                                key={index}
                                sx={{
                                  "&:nth-of-type(even)": {
                                    backgroundColor: "#f5f5f5",
                                  },
                                  "&:hover": {
                                    backgroundColor: "#e3f2fd",
                                  },
                                }}
                              >
                                <TableCell sx={{ fontWeight: "medium", fontSize: "0.95rem" }}>
                                  {installment.installmentNumber}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.95rem" }}>{feeTypeDisplay}</TableCell>
                                <TableCell sx={{ fontWeight: "medium", fontSize: "0.95rem" }}>
                                  ₹{installment.amount?.toLocaleString("en-IN") || "0"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.95rem" }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 16, color: "#666" }} />
                                    {formattedDate}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t("no_installments") || "No installments"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: "#f8f9fb", borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={handleCloseViewModal}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "#1976d2" },
              color: "white",
              px: 3,
            }}
          >
            {t("close") || "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* All Classes Fee Structure Dialog */}
      <Dialog
        open={allViewModalOpen}
        onClose={handleCloseAllViewModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f5f7fb",
            borderBottom: "2px solid #e0e7ff",
            pb: 1.5,
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ color: "#1a237e" }}>
            {t("all_fee_structures") || "All Fee Structures"}
          </Typography>
          <IconButton onClick={handleCloseAllViewModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: "#ffffff" }}>
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#1a237e", fontSize: "30px" }}>
                {schoolData?.nameofSchool?.toUpperCase() || "SCHOOL NAME"}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", fontSize: "18px" }}>
                {schoolData?.schoolCode && (
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    <strong>School Code:</strong> {schoolData.schoolCode}
                  </Typography>
                )}
                {schoolData?.diseCode && (
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    <strong>DISE Code:</strong> {schoolData.diseCode}
                  </Typography>
                )}
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1976d2" }}>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      {t("class") || "Class"}
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      {t("session") || "Session"}
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      {t("fee_type") || "Fee Type"}
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      {t("total_annual_fees") || "Total Annual Fees"}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feeStructures.length > 0 ? (
                    feeStructures.map((fs, idx) => (
                      <TableRow
                        key={`${fs.classId}-${fs.sessionId}-${idx}`}
                        sx={{
                          "&:nth-of-type(even)": { backgroundColor: "#f9fafc" },
                        }}
                      >
                        <TableCell>{fs.className || "-"}</TableCell>
                        <TableCell>{fs.sessionName || "-"}</TableCell>
                        <TableCell>{fs.feeType === "TUTION_FEES" ? "TUTION FEES" : fs.feeType || "-"}</TableCell>
                        <TableCell>₹{fs.totalAnnualFees?.toLocaleString("en-IN") || "0"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {t("no_data_found") || "No data found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: "#f8f9fb", borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={handleCloseAllViewModal}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "#1976d2" },
              color: "white",
              px: 3,
            }}
          >
            {t("close") || "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("confirm_delete") || "Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("are_you_sure_you_want_to_delete_this_fee_structure") ||
              "Are you sure you want to delete this fee structure? This action cannot be undone."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, color: "white" }}
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            autoFocus
            disabled={deleteFeeStructureMutation.isLoading}
          >
            {deleteFeeStructureMutation.isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t("deleting") || "Deleting..."}
              </>
            ) : (
              t("delete") || "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeeStructureManagement;

